import { T } from '../styles/tokens';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import i18next from 'i18next';
import { api, ApiError } from '../api/client';
import { Button, Input } from '../components/ui';
import { TenantLogo, tenantName } from '../components/TenantLogo';

// Página 100% hardcoded — zero consultas à API (padrão ChatGPT)
// Clona visualmente o ChatHub + Sidebar.tsx reais
// Quando o usuário tenta interagir, abre modal de login

// ═══ TYPEWRITER — clone EXATO do ChatHubPage.tsx ═══
// Lazy — chamado dentro do useEffect, quando i18next já carregou
function getGreetingPhrases() {
  return [
    i18next.t('chat:greeting_1', { defaultValue: 'Como posso ajudar?' }),
    i18next.t('chat:greeting_6', { defaultValue: 'O que você quer criar hoje?' }),
    i18next.t('chat:greeting_2', { defaultValue: 'Precisa de algo?' }),
    i18next.t('chat:greeting_3', { defaultValue: 'No que posso ajudar?' }),
    i18next.t('chat:greeting_4', { defaultValue: 'Qual é a ideia de hoje?' }),
    i18next.t('chat:greeting_5', { defaultValue: 'Vamos construir algo?' }),
  ];
}

function useTypewriter() {
  const [display, setDisplay] = useState('');
  const phraseIdx = useRef(0);
  const charIdx = useRef(0);
  const deleting = useRef(false);
  const timer = useRef<any>(null);

  useEffect(() => {
    const phrases = getGreetingPhrases();
    const tick = () => {
      const phrase = phrases[phraseIdx.current];
      if (!deleting.current) {
        charIdx.current++;
        setDisplay(phrase.slice(0, charIdx.current));
        if (charIdx.current >= phrase.length) {
          timer.current = setTimeout(() => { deleting.current = true; tick(); }, 7000);
          return;
        }
        timer.current = setTimeout(tick, 45 + Math.random() * 35);
      } else {
        charIdx.current--;
        setDisplay(phrase.slice(0, charIdx.current));
        if (charIdx.current <= 0) {
          deleting.current = false;
          phraseIdx.current = (phraseIdx.current + 1) % phrases.length;
          timer.current = setTimeout(tick, 400);
          return;
        }
        timer.current = setTimeout(tick, 25);
      }
    };
    tick();
    return () => clearTimeout(timer.current);
  }, []);

  return display;
}

// ═══ MODELOS DISPONÍVEIS (hardcoded) ═══
// Lazy — chamado dentro do componente pra garantir que i18next já carregou
function getModels() {
  return [
    { id: 'auto', name: i18next.t('chat:model_auto', { defaultValue: 'Automático' }), desc: i18next.t('chat:model_auto_desc', { defaultValue: 'Seleciona o melhor modelo para cada tarefa' }), icon: '🌐', badge: null },
    { id: 'claude-sonnet', name: 'Claude Sonnet 4.5', desc: i18next.t('chat:model_sonnet_desc', { defaultValue: 'Rápido e inteligente para o dia a dia' }), icon: null, badge: null },
    { id: 'claude-opus', name: 'Claude Opus 4.6', desc: i18next.t('chat:model_opus_desc', { defaultValue: 'Máximo poder de raciocínio' }), icon: null, badge: 'PRO' },
    { id: 'gpt-4o', name: 'GPT-4o', desc: i18next.t('chat:model_gpt4o_desc', { defaultValue: 'OpenAI mais versátil' }), icon: null, badge: null },
    { id: 'gemini-flash', name: 'Gemini 2.5 Flash', desc: i18next.t('chat:model_flash_desc', { defaultValue: 'Google ultra-rápido' }), icon: null, badge: null },
    { id: 'gemini-pro', name: 'Gemini 2.5 Pro', desc: i18next.t('chat:model_pro_desc', { defaultValue: 'Google avançado com reasoning' }), icon: null, badge: 'PRO' },
  ];
}

// ═══ ÍCONES — copiados EXATAMENTE do Sidebar.tsx (stroke-width 1.8) ═══

const IconMachines = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const IconChat = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
);

const IconPlug = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 2v6M8 2v6M16 2v6M5 8h14a1 1 0 011 1v2a6 6 0 01-6 6h0v5M13 17v5"/>
  </svg>
);

const IconReferral = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const IconSidebarCollapse = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/>
  </svg>
);

const IconPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconMic = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

const IconMenu = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const IconGlobe = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);

const IconUser = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconUserSmall = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

// ═══ NOVOS ÍCONES — Sidebar extras (ChatGPT-style) ═══

const IconImages = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
  </svg>
);

const IconApps = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const IconDeepResearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
  </svg>
);

const IconSearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconHelp = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

// Ícones para menu attach
const IconUpload = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const IconCamera = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/>
  </svg>
);

const IconLink = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
  </svg>
);

const IconCode = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
  </svg>
);

// Ícones para modal login
const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);

const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

const IconX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IconChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
);

function redirectToLogin() {
  route('/chat');
}

// ═══ TIPOS DE MODAL (mutual exclusion) ═══
type ModalType = 'none' | 'modelSelector' | 'attachMenu' | 'helpMenu' | 'loginModal';

export function PublicChatPage({ locale }: { locale?: string; path?: string; default?: boolean }) {
  // Mudar idioma se prop locale foi passada (rota /pt-BR, /en-US, etc.)
  useEffect(() => {
    if (locale && i18next.language !== locale) {
      i18next.changeLanguage(locale);
    }
  }, [locale]);

  const MODELS = getModels();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [activeModal, setActiveModal] = useState<ModalType>('none');
  const [selectedModel, setSelectedModel] = useState('auto');
  const [cookieDismissed, setCookieDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('wlc_cookie_consent') === '1';
    }
    return false;
  });
  const greetingText = useTypewriter();

  // Pré-carregar config de auth NO NÍVEL DA PÁGINA (antes de qualquer modal)
  // Assim o LoginModal já abre com config pronta = zero flash
  const [pageAuthCfg, setPageAuthCfg] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    api.get<any>('/auth/config').then((res: any) => {
      setPageAuthCfg(res || {});
    }).catch(() => setPageAuthCfg({}));
  }, []);

  // Pré-carregar GIS script + One Tap automático na homepage
  useEffect(() => {
    if (!pageAuthCfg || pageAuthCfg.google_enabled !== 'true' || !pageAuthCfg.google_client_id) return;
    const clientId = pageAuthCfg.google_client_id;
    const oneTapEnabled = pageAuthCfg.google_one_tap_enabled === 'true';

    const initGoogleOnPage = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: async (resp: any) => {
          try {
            await api.post('/auth/google', { credential: resp.credential });
            window.location.href = '/chat';
          } catch { /* usuário pode tentar de novo */ }
        },
      });
      // One Tap automático — abre sozinho no canto da tela
      if (oneTapEnabled) {
        window.google.accounts.id.prompt();
      }
    };

    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (!existingScript) {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = initGoogleOnPage;
      document.head.appendChild(s);
    } else if (window.google?.accounts) {
      initGoogleOnPage();
    } else {
      // Script existe mas ainda não carregou — esperar
      existingScript.addEventListener('load', initGoogleOnPage);
    }
  }, [pageAuthCfg]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fechar modals ao clicar fora
  useEffect(() => {
    if (activeModal === 'none') return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-modal-content]')) return;
      if (target.closest('[data-modal-trigger]')) return;
      setActiveModal('none');
    };
    // Delay para não fechar imediatamente
    const t = setTimeout(() => document.addEventListener('click', handleClick), 50);
    return () => { clearTimeout(t); document.removeEventListener('click', handleClick); };
  }, [activeModal]);

  const toggleModal = (modal: ModalType) => {
    setActiveModal(prev => prev === modal ? 'none' : modal);
  };

  const handleInteraction = (e?: Event) => {
    if (e) e.preventDefault();
    toggleModal('loginModal');
  };

  const handleCookieAccept = () => {
    setCookieDismissed(true);
    if (typeof window !== 'undefined') localStorage.setItem('wlc_cookie_consent', '1');
  };

  const currentModel = MODELS.find(m => m.id === selectedModel) || MODELS[0];

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', background: T.bg, color: T.text, fontFamily: T.fontFamily, overflow: 'hidden' }}>
      <style>{`
        @keyframes blink { 50% { border-color: transparent; } }
        @keyframes modalFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes overlayFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes loginModalIn { from { opacity: 0; transform: translate(-50%, -48%); } to { opacity: 1; transform: translate(-50%, -50%); } }
        @keyframes cookieSlide { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .nav-item-hover:hover { background: ${T.border} !important; }
        .btn-hover:hover { opacity: 0.85 !important; }
        .model-item:hover { background: ${T.border} !important; }
        .attach-item:hover { background: ${T.border} !important; }
        .help-item:hover { background: ${T.border} !important; }
      `}</style>

      {/* Overlay mobile sidebar */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{ position: 'fixed', inset: 0, background: T.bgOverlay, zIndex: 998 }}
        />
      )}

      {/* ═══ SIDEBAR ═══ */}
      <aside style={{
        width: 260, minWidth: 260, height: '100vh', background: 'var(--c-sidebar-bg, #171717)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        position: isMobile ? 'fixed' as const : 'relative' as const,
        left: isMobile ? (mobileMenuOpen ? 0 : -260) : 0,
        zIndex: 999, transition: 'left 0.2s ease',
      }}>
        {/* Logo + Toggle */}
        <div style={{ flexShrink: 0 }}>
          <div style={{ padding: '10px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <TenantLogo size={28} />
              <span style={{ fontSize: 16, fontWeight: 700, color: T.text, letterSpacing: '-0.3px', whiteSpace: 'nowrap' }}>{tenantName()}</span>
            </div>
            <div onClick={() => handleInteraction()} style={{ width: 28, height: 28, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary }}>
              <IconSidebarCollapse />
            </div>
          </div>
        </div>

        {/* Nav items — ETAPA 6: items extras */}
        <nav style={{ flex: 1, overflow: 'auto' }}>
          <NavItem icon={<IconMachines />} label={i18next.t('chat:manage_agents', { defaultValue: 'Gerenciar Agentes' })} onClick={() => handleInteraction()} />
          <NavItem icon={<IconChat />} label={i18next.t('chat:new_chat', { defaultValue: 'Novo Chat' })} onClick={() => handleInteraction()} active />
          <NavItem icon={<IconPlug />} label={i18next.t('chat:integrations', { defaultValue: 'Integrações' })} onClick={() => handleInteraction()} />
          <NavItem icon={<IconReferral />} label={i18next.t('chat:refer_earn', { defaultValue: 'Indique & Ganhe' })} onClick={() => handleInteraction()} />

          {/* Seção "Seus chats" */}
          <div style={{ marginTop: 16, padding: '0 16px', marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{i18next.t('chat:your_chats', { defaultValue: 'Seus chats' })}</span>
          </div>
          <div style={{ padding: '8px 24px', color: T.textSecondary, fontSize: 13 }}>
            {i18next.t('chat:login_to_see_chats', { defaultValue: 'Faça login para ver seus chats' })}
          </div>
        </nav>

        {/* ETAPA 5: CTA melhorado — texto persuasivo + botão */}
        <div style={{ padding: '16px', flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <p style={{ fontSize: 12, color: T.textSecondary, margin: '0 0 10px', lineHeight: 1.4 }}>
            {i18next.t('chat:personalized_responses', { defaultValue: 'Respostas personalizadas, histórico de chats e criação de imagens.' })}
          </p>
          <Button
            variant="primary"
            onClick={() => toggleModal('loginModal')}
            fullWidth
            icon={<IconUserSmall />}
            style={{ borderRadius: 8, fontSize: 14 }}
          >
            {i18next.t('chat:sign_in_create_account', { defaultValue: 'Entrar / Criar conta' })}
          </Button>
        </div>
      </aside>

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', minWidth: 0, background: T.bg, overflow: 'hidden' }}>

        {/* ═══ HEADER — ETAPA 3: Login/Signup buttons + ícone ? ═══ */}
        {!isMobile ? (
          <div style={{
            padding: '0 16px', height: 48, borderBottom: `1px solid ${T.border}`,
            background: T.bg, display: 'flex', alignItems: 'center', flexShrink: 0,
            position: 'relative',
          }}>
            {/* Esquerda: ElonChat + dropdown modelo — ETAPA 1 */}
            <div style={{ position: 'relative' }}>
              <Button variant="ghost"
                data-modal-trigger
                onClick={() => toggleModal('modelSelector')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  background: 'transparent', border: 'none', padding: '6px 12px', borderRadius: 8,
                  color: T.text, fontSize: 18, fontWeight: 700,
                }}
                className="btn-hover"
              >
                {tenantName()}
                <span style={{ color: T.textSecondary, fontWeight: 400, fontSize: 14 }}> · {currentModel.name}</span>
                <IconChevronDown />
              </Button>

              {/* Modal Seletor de Modelo */}
              {activeModal === 'modelSelector' && (
                <div data-modal-content style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4,
                  width: 340, background: T.bgModal, borderRadius: 12,
                  border: `1px solid ${T.bgActive}`, boxShadow: `0 8px 32px ${T.bgOverlay}`,
                  padding: '8px 0', zIndex: 1001,
                  animation: 'modalFadeIn 0.15s ease-out',
                }}>
                  <div style={{ padding: '8px 16px 4px', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{i18next.t('chat:select_model', { defaultValue: 'Selecionar modelo' })}</span>
                  </div>
                  {MODELS.map(m => (
                    <div
                      key={m.id}
                      class="model-item"
                      onClick={() => { setSelectedModel(m.id); setActiveModal('none'); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                        cursor: 'pointer', transition: 'background 0.1s',
                        background: selectedModel === m.id ? T.bgHover : 'transparent',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 500, color: T.text }}>
                            {m.icon && <span style={{ marginRight: 4 }}>{m.icon}</span>}
                            {m.name}
                          </span>
                          {m.badge && (
                            <span style={{ fontSize: 9, fontWeight: 700, background: T.gradientAccent, color: T.text, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.3px' }}>{m.badge}</span>
                          )}
                        </div>
                        <span style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.3 }}>{m.desc}</span>
                      </div>
                      {selectedModel === m.id && (
                        <span style={{ color: T.primary, flexShrink: 0 }}><IconCheck /></span>
                      )}
                    </div>
                  ))}
                  <div style={{ borderTop: `1px solid ${T.border}`, margin: '4px 0' }} />
                  <div style={{ padding: '8px 16px', fontSize: 12, color: T.textSecondary, lineHeight: 1.4 }}>
                    {i18next.t('chat:pro_models_require_plan', { defaultValue: 'Modelos PRO requerem plano pago.' })}
                    <span onClick={() => toggleModal('loginModal')} style={{ color: T.primary, cursor: 'pointer', marginLeft: 4 }}>{i18next.t('chat:do_upgrade', { defaultValue: 'Fazer upgrade' })}</span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ flex: 1 }} />

            {/* Direita: Entrar + Criar conta + ? — ETAPA 3 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => toggleModal('loginModal')}
                style={{ background: T.text, color: T.bgSecondary, borderRadius: 8, fontSize: 13, padding: '6px 16px', boxShadow: 'none' }}
              >
                {i18next.t('chat:sign_in', { defaultValue: 'Entrar' })}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleModal('loginModal')}
                style={{ borderRadius: 8, fontSize: 13, fontWeight: 500, padding: '6px 16px', color: T.text, border: '1px solid rgba(255,255,255,0.2)' }}
              >
                {i18next.t('chat:create_free_account', { defaultValue: 'Criar conta grátis' })}
              </Button>
              <div style={{ position: 'relative' }}>
                <Button variant="ghost"
                  data-modal-trigger
                  onClick={() => toggleModal('helpMenu')}
                  style={{
                    width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none', color: T.textSecondary,
                  }}
                  className="btn-hover"
                >
                  <IconHelp />
                </Button>

                {/* ETAPA 3: Menu Ajuda */}
                {activeModal === 'helpMenu' && (
                  <div data-modal-content style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    width: 220, background: T.bgModal, borderRadius: 10,
                    border: `1px solid ${T.bgActive}`, boxShadow: `0 8px 32px ${T.bgOverlay}`,
                    padding: '6px 0', zIndex: 1001,
                    animation: 'modalFadeIn 0.15s ease-out',
                  }}>
                    <HelpMenuItem label={i18next.t('chat:help_center', { defaultValue: 'Central de Ajuda' })} onClick={() => handleInteraction()} />
                    <HelpMenuItem label={i18next.t('chat:documentation', { defaultValue: 'Documentação' })} onClick={() => { window.open('/developers', '_blank'); setActiveModal('none'); }} />
                    <HelpMenuItem label={i18next.t('chat:system_status', { defaultValue: 'Status do Sistema' })} onClick={() => handleInteraction()} />
                    <div style={{ borderTop: `1px solid ${T.border}`, margin: '4px 0' }} />
                    <HelpMenuItem label={i18next.t('chat:terms_of_use', { defaultValue: 'Termos de Uso' })} onClick={() => handleInteraction()} />
                    <HelpMenuItem label={i18next.t('chat:privacy_policy', { defaultValue: 'Política de Privacidade' })} onClick={() => handleInteraction()} />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Mobile header */
          <div style={{
            padding: '0 16px 0 8px', height: 48, borderBottom: `1px solid ${T.border}`,
            background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button variant="ghost" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{ background: 'none', border: 'none', color: T.text, cursor: 'pointer', padding: 4, display: 'flex' }}>
                <IconMenu />
              </Button>
              <Button variant="ghost"
                data-modal-trigger
                onClick={() => toggleModal('modelSelector')}
                style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: T.text, cursor: 'pointer', padding: 0 }}
              >
                <span style={{ fontSize: 16, fontWeight: 700 }}>{tenantName()}</span>
                <IconChevronDown />
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => toggleModal('loginModal')}
                style={{ background: T.text, color: T.bgSecondary, borderRadius: 8, fontSize: 13, fontWeight: 600, padding: '6px 16px', boxShadow: 'none' }}
              >
                {i18next.t('chat:sign_in', { defaultValue: 'Entrar' })}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleModal('loginModal')}
                style={{ borderRadius: 8, fontSize: 12, fontWeight: 500, padding: '5px 14px', color: T.text, border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {i18next.t('chat:create_account_short', { defaultValue: 'Criar conta' })}
              </Button>
            </div>

            {/* Mobile model selector & help menu — posição absoluta */}
            {activeModal === 'modelSelector' && (
              <div data-modal-content style={{
                position: 'fixed', top: 52, left: 8, right: 8,
                background: T.bgModal, borderRadius: 12,
                border: `1px solid ${T.bgActive}`, boxShadow: `0 8px 32px ${T.bgOverlay}`,
                padding: '8px 0', zIndex: 1001, animation: 'modalFadeIn 0.15s ease-out',
              }}>
                <div style={{ padding: '8px 16px 4px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Selecionar modelo</span>
                </div>
                {MODELS.map(m => (
                  <div
                    key={m.id} class="model-item"
                    onClick={() => { setSelectedModel(m.id); setActiveModal('none'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                      cursor: 'pointer', background: selectedModel === m.id ? T.bgHover : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: T.text }}>{m.icon && <span style={{ marginRight: 4 }}>{m.icon}</span>}{m.name}</span>
                        {m.badge && <span style={{ fontSize: 9, fontWeight: 700, background: T.gradientAccent, color: T.text, padding: '1px 6px', borderRadius: 4 }}>{m.badge}</span>}
                      </div>
                      <span style={{ fontSize: 12, color: T.textSecondary }}>{m.desc}</span>
                    </div>
                    {selectedModel === m.id && <span style={{ color: T.primary }}><IconCheck /></span>}
                  </div>
                ))}
              </div>
            )}

            {activeModal === 'helpMenu' && (
              <div data-modal-content style={{
                position: 'fixed', top: 52, right: 8, width: 220,
                background: T.bgModal, borderRadius: 10,
                border: `1px solid ${T.bgActive}`, boxShadow: `0 8px 32px ${T.bgOverlay}`,
                padding: '6px 0', zIndex: 1001, animation: 'modalFadeIn 0.15s ease-out',
              }}>
                <HelpMenuItem label="Central de Ajuda" onClick={() => handleInteraction()} />
                <HelpMenuItem label="Documentação" onClick={() => { window.open('/developers', '_blank'); setActiveModal('none'); }} />
                <HelpMenuItem label="Termos de Uso" onClick={() => handleInteraction()} />
                <HelpMenuItem label={i18next.t('chat:privacy_policy', { defaultValue: 'Privacidade' })} onClick={() => handleInteraction()} />
              </div>
            )}
          </div>
        )}

        {/* Área central — greeting + input + quick actions */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 20px', position: 'relative' }}>
          {/* Greeting */}
          <span style={{ fontSize: 28, fontWeight: 600, color: T.text, textAlign: 'center', marginBottom: 24, minHeight: '1.2em' }}>
            {greetingText}<span style={{ borderRight: `2px solid ${T.text}`, marginLeft: 1, animation: 'blink 1s step-end infinite' }} />
          </span>

          {/* Input wrapper */}
          <div style={{ width: '100%', maxWidth: isMobile ? '100%' : 700 }}>
            <div style={{ background: T.bgCardHover, borderRadius: 24, position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 8px 8px 4px', gap: 4 }}>
                {/* ETAPA 2: Botão + com menu attach */}
                <div style={{ position: 'relative' }}>
                  <Button variant="ghost"
                    data-modal-trigger
                    onClick={() => toggleModal('attachMenu')}
                    style={{ width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: T.textSecondary, padding: 0, flexShrink: 0 }}
                  >
                    <IconPlus />
                  </Button>

                  {/* Menu Attach */}
                  {activeModal === 'attachMenu' && (
                    <div data-modal-content style={{
                      position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
                      width: 240, background: T.bgModal, borderRadius: 10,
                      border: `1px solid ${T.bgActive}`, boxShadow: `0 8px 32px ${T.bgOverlay}`,
                      padding: '6px 0', zIndex: 1001,
                      animation: 'modalFadeIn 0.15s ease-out',
                    }}>
                      <AttachMenuItem icon={<IconUpload />} label={i18next.t('chat:upload_file', { defaultValue: 'Enviar arquivo' })} onClick={() => handleInteraction()} />
                      <AttachMenuItem icon={<IconCamera />} label={i18next.t('chat:take_photo', { defaultValue: 'Tirar foto' })} onClick={() => handleInteraction()} />
                      <AttachMenuItem icon={<IconLink />} label={i18next.t('chat:paste_link', { defaultValue: 'Colar link' })} onClick={() => handleInteraction()} />
                      <AttachMenuItem icon={<IconCode />} label={i18next.t('chat:code', { defaultValue: 'Código' })} onClick={() => handleInteraction()} />
                      <AttachMenuItem icon={<IconImages />} label={i18next.t('chat:generate_image', { defaultValue: 'Gerar imagem' })} onClick={() => handleInteraction()} />
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  placeholder={i18next.t('chat:ask_something', { defaultValue: 'Pergunte alguma coisa' })}
                  value={inputValue}
                  onInput={(e) => setInputValue((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleInteraction(e); }}
                  style={{
                    flex: 1, background: 'transparent', border: 'none',
                    fontSize: 16, padding: '0 8px', color: T.text,
                    lineHeight: 1.5, outline: 'none', fontFamily: T.fontFamily,
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {!isMobile && (
                    <Button variant="ghost"
                      data-modal-trigger
                      onClick={() => toggleModal('modelSelector')}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px',
                        fontSize: 13, fontWeight: 600, color: T.textSecondary,
                        display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8,
                      }}
                      className="btn-hover"
                    >
                      <IconGlobe /> {currentModel.name}
                      <IconChevronDown />
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => handleInteraction()} style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                    display: !isMobile ? 'flex' : (!inputValue.trim() ? 'flex' : 'none'),
                    alignItems: 'center', justifyContent: 'center',
                    background: 'transparent', border: 'none', color: T.textSecondary, padding: 0,
                  }}>
                    <IconMic />
                  </Button>
                  <Button variant="ghost" onClick={() => handleInteraction()} style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: inputValue.trim() ? T.text : T.textMuted, border: 'none', padding: 0,
                    transition: 'all 0.2s',
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick actions — desktop only */}
            {!isMobile && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative' }}>
                  <Button variant="ghost" onClick={() => handleInteraction()} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20,
                    border: `1px solid ${T.borderHover}`, background: 'transparent',
                    color: T.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: T.fontFamily,
                  }} className="btn-hover">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 00-16 0"/><line x1="12" y1="4" x2="12" y2="12"/><line x1="8" y1="8" x2="16" y2="8"/></svg>
                    {i18next.t('chat:create_agent', { defaultValue: 'Criar Agente' })}
                  </Button>
                  <span style={{ position: 'absolute', top: -6, right: -8, fontSize: 8, fontWeight: 700, background: T.warning, color: 'currentColor', padding: '2px 5px', borderRadius: 6, letterSpacing: 0.5, lineHeight: '12px', pointerEvents: 'none' }}>{i18next.t('chat:new_badge', { defaultValue: 'NOVO' })}</span>
                </div>
                <QuickAction icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>} label={i18next.t('chat:create_images', { defaultValue: 'Crie imagens' })} onClick={() => handleInteraction()} />
                <QuickAction icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>} label={i18next.t('chat:write_or_edit', { defaultValue: 'Escreva ou edite' })} onClick={() => handleInteraction()} />
                <QuickAction icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>} label={i18next.t('chat:look_up', { defaultValue: 'Consulte algo' })} onClick={() => handleInteraction()} />
              </div>
            )}
          </div>

          {/* ETAPA 5: Footer legal */}
          <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center' }}>
            <span style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.4 }}>
              {i18next.t('chat:footer_terms', { defaultValue: `Ao usar o ${tenantName()}, você concorda com nossos`, appName: tenantName() })}{' '}
              <a href="/terms" style={{ color: T.textSecondary, textDecoration: 'underline' }}>{i18next.t('chat:footer_terms_link', { defaultValue: 'Termos' })}</a>
              {' '}{i18next.t('chat:footer_privacy_prefix', { defaultValue: 'e confirma ter lido nossa' })}{' '}
              <a href="/privacy" style={{ color: T.textSecondary, textDecoration: 'underline' }}>{i18next.t('chat:footer_privacy_link', { defaultValue: 'Política de Privacidade' })}</a>.
            </span>
          </div>
        </div>
      </div>

      {/* ═══ ETAPA 4: MODAL LOGIN — 100% FUNCIONAL ═══ */}
      {activeModal === 'loginModal' && (
        <LoginModal isMobile={isMobile} onClose={() => setActiveModal('none')} preloadedAuthCfg={pageAuthCfg} />
      )}

      {/* ═══ ETAPA 5: COOKIE BANNER ═══ */}
      {!cookieDismissed && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: T.bgSecondary, borderTop: `1px solid ${T.bgHover}`,
          padding: isMobile ? '14px 16px' : '14px 24px',
          display: 'flex', alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 12 : 16,
          zIndex: 1500,
          animation: 'cookieSlide 0.3s ease-out',
        }}>
          <p style={{ margin: 0, fontSize: 13, color: T.textSecondary, lineHeight: 1.5, flex: 1, maxWidth: isMobile ? '100%' : 700 }}>
            {i18next.t('chat:cookie_text', { defaultValue: 'Usamos cookies para manter o site funcionando, entender como você usa e melhorar sua experiência.' })}{' '}
            <a href="/privacy" style={{ color: T.textSecondary, textDecoration: 'underline' }}>{i18next.t('chat:cookie_policy', { defaultValue: 'Política de Cookies' })}</a>.
          </p>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCookieAccept}
              style={{ borderRadius: 8, fontSize: 13, fontWeight: 500, padding: '8px 16px', color: T.text, border: '1px solid rgba(255,255,255,0.2)' }}
            >
              {i18next.t('chat:reject_optional', { defaultValue: 'Rejeitar opcionais' })}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleCookieAccept}
              style={{ background: T.text, color: T.bgSecondary, borderRadius: 8, fontSize: 13, padding: '8px 16px', boxShadow: 'none' }}
            >
              {i18next.t('chat:accept_all', { defaultValue: 'Aceitar todos' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ LOGIN MODAL — FUNCIONAL (usa API real) ═══

function LoginModal({ isMobile, onClose, preloadedAuthCfg }: { isMobile: boolean; onClose: () => void; preloadedAuthCfg?: Record<string, string> | null }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  // Usar config pré-carregada (zero flash) ou carregar como fallback
  const [authCfg, setAuthCfg] = useState<Record<string, string> | null>(preloadedAuthCfg || null);

  useEffect(() => {
    setTimeout(() => emailRef.current?.focus(), 200);
    // Só buscar se não veio pré-carregada
    if (!preloadedAuthCfg) {
      api.get<any>('/auth/config').then((res: any) => {
        setAuthCfg(res || {});
      }).catch(() => setAuthCfg({}));
    }
  }, []);

  // Resetar loading quando janela ganha foco (popup Google fechou sem login)
  useEffect(() => {
    const onFocus = () => { setGoogleLoading(false); };
    const onVisChange = () => { if (document.visibilityState === 'visible') setGoogleLoading(false); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisChange);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisChange); };
  }, []);

  // Google Sign-In
  useEffect(() => {
    if (!authCfg || authCfg.google_enabled !== 'true' || !authCfg.google_client_id) return;
    const cid = authCfg.google_client_id;
    const initG = () => {
      if (!window.google?.accounts) return;
      window.google.accounts.id.initialize({ client_id: cid, auto_select: false, cancel_on_tap_outside: true,
        intermediate_iframe_close_callback: () => { setGoogleLoading(false); },
        callback: async (resp: any) => {
          setLoading(true); setGoogleLoading(true); setError('');
          try {
            await api.post('/auth/google', { credential: resp.credential });
            window.location.href = '/chat';
          } catch (err) {
            setError(err instanceof ApiError ? err.message : i18next.t('auth:error_google_login'));
            setGoogleLoading(false);
          } finally { setLoading(false); }
        },
      });
      // Renderizar botão GIS real (transparente, recebe clique real do usuário)
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard', size: 'large', text: 'continue_with', width: 400, theme: 'filled_black',
          click_listener: () => { setGoogleLoading(true); },
        });
      }
    };
    // GIS script já foi carregado no nível da página
    if (window.google?.accounts) { initG(); }
    else {
      // Fallback: esperar script carregar
      const interval = setInterval(() => {
        if (window.google?.accounts) { clearInterval(interval); initG(); }
      }, 100);
      setTimeout(() => clearInterval(interval), 5000);
    }
  }, [authCfg]);

  const handleMagicLink = async (e: Event) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setLoading(true);
    try {
      const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value ?? 'dev-token';
      await api.post('/auth/magic-link', { email, turnstileToken });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : i18next.t('auth:error_send_link'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: Event) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setError('');
    setLoading(true);
    try {
      const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value ?? 'dev-token';
      const res = await api.post<any>('/auth/login-password', { email, password, turnstileToken });
      if (res?.requires_2fa && res?.tempToken) {
        // Redirecionar para callback com tempToken para 2FA
        window.location.href = `/auth/callback?2fa=1&tempToken=${encodeURIComponent(res.tempToken)}`;
        return;
      }
      window.location.href = '/chat';
    } catch (err) {
      setError(err instanceof ApiError ? err.message : i18next.t('auth:error_wrong_credentials'));
    } finally {
      setLoading(false);
    }
  };

  // ═══ ESTADO: Email enviado ═══
  if (sent) {
    return (
      <>
        <div style={{
          position: 'fixed', inset: 0, background: T.bgOverlay,
          zIndex: 2000, animation: 'overlayFadeIn 0.15s ease-out',
        }} onClick={onClose} />
        <div data-modal-content style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: isMobile ? 'calc(100% - 32px)' : 420, background: T.bgModal, borderRadius: 16,
          border: `1px solid ${T.bgActive}`, boxShadow: `0 16px 64px ${T.bgOverlay}`,
          zIndex: 2001, animation: 'loginModalIn 0.2s ease-out', willChange: 'opacity, transform', overflow: 'hidden',
        }}>
          <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={onClose} style={{
              width: 32, height: 32, borderRadius: 8, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', background: T.bgCardHover,
              border: 'none', color: T.textSecondary,
            }} className="btn-hover"><IconX /></Button>
          </div>
          <div style={{ padding: '8px 24px 28px', textAlign: 'center' }}>
            {/* Ícone de envio */}
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
              background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.primary} stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/>
              </svg>
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: T.text }}>{i18next.t('chat:email_sent_title', { defaultValue: 'Email enviado!' })}</h2>
            <p style={{ fontSize: 14, color: T.textSecondary, margin: '0 0 16px', lineHeight: 1.5 }}>
              {i18next.t('chat:magic_link_sent', { defaultValue: 'Enviamos um link mágico para' })}
            </p>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: T.primaryBg, border: `1px solid ${T.primaryBgStrong}`,
              borderRadius: 8, padding: '8px 16px', marginBottom: 20,
            }}>
              <IconMail />
              <span style={{ color: T.primary, fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}>{email}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 16 }}>
              <div style={{
                flex: 1, maxWidth: 140, background: T.bgCard, borderRadius: 8,
                padding: '10px', border: `1px solid ${T.border}`,
              }}>
                <div style={{ fontSize: 10, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 4 }}>{i18next.t('chat:expires_in', { defaultValue: 'Expira em' })}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>15 min</div>
              </div>
              <div style={{
                flex: 1, maxWidth: 140, background: T.bgCard, borderRadius: 8,
                padding: '10px', border: `1px solid ${T.border}`,
              }}>
                <div style={{ fontSize: 10, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 4 }}>{i18next.t('chat:not_received', { defaultValue: 'Não chegou?' })}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.textSecondary }}>{i18next.t('chat:check_spam', { defaultValue: 'Verifique o spam' })}</div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSent(false); setError(''); }}
              style={{ borderRadius: 8, padding: '10px 24px', background: T.border, border: `1px solid ${T.bgActive}`, color: T.textSecondary, fontSize: 13, fontWeight: 500 }}
            >
              {i18next.t('chat:try_another_email', { defaultValue: 'Tentar outro email' })}
            </Button>
          </div>
        </div>
      </>
    );
  }

  // ═══ ESTADO: Formulário ═══
  return (
    <>
      <div style={{
        position: 'fixed', inset: 0, background: T.bgOverlay,
        zIndex: 2000, animation: 'overlayFadeIn 0.15s ease-out',
      }} onClick={onClose} />
      <div data-modal-content style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: isMobile ? 'calc(100% - 32px)' : 420, background: T.bgModal, borderRadius: 16,
        border: `1px solid ${T.bgActive}`, boxShadow: `0 16px 64px ${T.bgOverlay}`,
        zIndex: 2001, animation: 'loginModalIn 0.2s ease-out', willChange: 'opacity, transform', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.text }}>{i18next.t('chat:welcome_title', { defaultValue: `Bem-vindo ao ${tenantName()}`, appName: tenantName() })}</h2>
            <p style={{ margin: '6px 0 0', fontSize: 14, color: T.textSecondary }}>{i18next.t('chat:welcome_subtitle', { defaultValue: 'Entre ou crie sua conta para continuar' })}</p>
          </div>
          <Button variant="ghost" onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', background: T.bgCardHover,
            border: 'none', color: T.textSecondary, flexShrink: 0,
          }} className="btn-hover"><IconX /></Button>
        </div>

        {/* Conteúdo */}
        <div style={{ padding: '20px 24px 24px' }}>
          {/* Erro */}
          {error && (
            <div style={{
              background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
              borderRadius: 8, padding: '10px 14px', marginBottom: 14,
              fontSize: 13, color: T.danger, lineHeight: 1.4,
            }}>
              {error}
            </div>
          )}

          {/* Botão Google Sign-In — wrapper com nosso design + botão GIS real por baixo */}
          {authCfg?.google_enabled === 'true' && authCfg?.google_client_id && (
            <>
              <div data-google-btn style={{ position: 'relative', width: '100%', height: 44, marginBottom: 4 }}>
                {/* Nosso visual (não-interativo, só aparência) */}
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: 10,
                  border: `1px solid ${T.borderHover}`, background: T.bgCard,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  pointerEvents: 'none', zIndex: 1,
                }}>
                  {googleLoading ? (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite', color: T.textMuted }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                      </svg>
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.textSecondary }}>{i18next.t('chat:loading_text', { defaultValue: 'Carregando...' })}</span>
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.43l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{i18next.t('chat:continue_with_google', { defaultValue: 'Continuar com Google' })}</span>
                    </>
                  )}
                </div>
                {/* Botão GIS real (transparente, recebe clique real — abre popup) */}
                <div ref={googleBtnRef} style={{
                  position: 'absolute', inset: 0, zIndex: 2, opacity: 0.01, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
                <div style={{ flex: 1, height: 1, background: T.bgHover }} />
                <span style={{ fontSize: 12, color: T.textMuted }}>ou</span>
                <div style={{ flex: 1, height: 1, background: T.bgHover }} />
              </div>
            </>
          )}

          {/* Form magic link */}
          <form onSubmit={showPassword ? handlePasswordLogin : handleMagicLink}>
            {/* Email */}
            <div style={{ marginBottom: 12 }}>
              <Input
                label="Email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={setEmail}
                icon={<IconMail />}
                autoFocus
              />
            </div>

            {/* Senha — aparece quando clica "Entrar com senha" */}
            {showPassword && (
              <div style={{ marginBottom: 12 }}>
                <Input
                  label="Senha"
                  type="password"
                  placeholder="Sua senha"
                  value={password}
                  onChange={setPassword}
                  icon={<IconLock />}
                />
              </div>
            )}

            {/* Turnstile placeholder (invisível, usa dev-token se não existir) */}
            <div class="cf-turnstile" data-sitekey={authCfg?.turnstile_site_key || '1x00000000000000000000AA'} data-theme="dark" style={{ marginTop: 8 }} />

            {/* Botão principal */}
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              loading={loading}
              fullWidth
              style={{ borderRadius: 10, fontSize: 15, padding: '12px', marginTop: 8, background: loading ? T.primaryBg : T.primary }}
            >
              {loading ? (
                i18next.t('auth:sending')
              ) : showPassword ? (
                <>
                  <IconLock />
                  {i18next.t('auth:enter', { defaultValue: 'Entrar' })}
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="none">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                  {i18next.t('chat:enter_magic_link', { defaultValue: 'Entrar com Magic Link' })}
                </>
              )}
            </Button>
          </form>

          {/* Toggle senha / magic link */}
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowPassword(!showPassword); setError(''); }}
              style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 13, fontWeight: 500, padding: '6px 0' }}
            >
              {showPassword ? i18next.t('auth:back_to_magic_link') : i18next.t('auth:enter_with_password')}
            </Button>
          </div>

          {/* Separador fino */}
          <div style={{ height: 1, background: T.border, margin: '14px 0' }} />

          {/* Link criar conta */}
          <p style={{ textAlign: 'center', fontSize: 13, color: T.textSecondary, margin: 0 }}>
            {i18next.t('chat:no_account', { defaultValue: 'Não tem conta?' })}{' '}
            <span onClick={redirectToLogin} style={{ color: T.primary, cursor: 'pointer', fontWeight: 600 }}>{i18next.t('chat:create_free_account', { defaultValue: 'Criar conta grátis' })}</span>
          </p>
        </div>
      </div>
    </>
  );
}

// ═══ COMPONENTES AUXILIARES ═══

function NavItem({ icon, label, onClick, active = false }: { icon: any; label: string; onClick: () => void; active?: boolean }) {
  return (
    <div
      onClick={onClick}
      class="nav-item-hover"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
        borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 400,
        color: active ? T.text : T.textSecondary,
        background: active ? T.bgHover : 'transparent',
        transition: 'background 0.15s', margin: '1px 8px',
      }}
    >
      <span style={{ width: 18, display: 'flex', justifyContent: 'center', flexShrink: 0, opacity: 0.85 }}>{icon}</span>
      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </div>
  );
}

function QuickAction({ icon, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      icon={icon}
      style={{
        borderRadius: 20, padding: '8px 16px',
        border: `1px solid ${T.borderHover}`, background: 'transparent',
        color: T.text, fontSize: 13, fontWeight: 500, fontFamily: T.fontFamily,
      }}
    >
      {label}
    </Button>
  );
}

function HelpMenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <div onClick={onClick} class="help-item" style={{
      padding: '8px 16px', cursor: 'pointer', fontSize: 13, color: T.text,
      transition: 'background 0.1s',
    }}>
      {label}
    </div>
  );
}

function AttachMenuItem({ icon, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  return (
    <div onClick={onClick} class="attach-item" style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
      cursor: 'pointer', fontSize: 13, color: T.text, transition: 'background 0.1s',
    }}>
      <span style={{ color: T.textSecondary, display: 'flex' }}>{icon}</span>
      {label}
    </div>
  );
}
